// The water card's template, card.water (contract §8.3): WaterCardView
// (cloud/web/src/components/board/BoardCards.tsx) through one `water` feed
// (§4.8): the hero and its "of", the fullness words, the attention line, the
// levels-only note, "No reading since", the forecast, and six tank rows
// (name, bar, value and unit, age), coloured by severity. Its card is the
// status card with the water icon and the feed's "as of" (§6.2).
//
// A row with no tank is hidden: its name is empty. The first row also holds
// the note shown when the boat reports no fresh water tanks at all. The hero
// takes no negative letter-spacing, which a version 2 page may not set
// (§9.2): today's -0.01em is 0 here.
//
// The Marina look (cloud ADR 0396, TOM-678): the card is water's
// (`data-domain`). Each tank row has the droplet in water's symbol colour
// beside its name, a neutral track and its fill in water's colour, and a
// tank past its line keeps that colour and shows the warning mark beside its
// value, in the warning's tone, with the attention line above the rows.

import { css, domainCss, h, output, warnMark, warnShape } from "./kit.js";

const ROWS = 6;

const CSS = css(`
  .wt { gap: 12px; }
  /* The hero's room (its 7 px for the numeral's glyphs and the gap under it)
     is its parts' own margins, so a card with no hero (a tank with no level)
     starts at its attention line, as today (TOM-665). */
  .wt-hero { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 8px; margin-bottom: -12px; }
  .wt-hero > * { margin-top: 7px; margin-bottom: 12px; }
  .wt-num { font: 700 46px/1 var(--tc-font-rounded); font-variant-numeric: tabular-nums;
    padding-block: 0.15em; margin-block: -0.15em; }
  .wt-hero > .wt-num { margin-top: calc(7px - 0.15em); margin-bottom: calc(12px - 0.15em); }
  .wt-num[data-quality=stale] { color: var(--muted); }
  .wt-u { font-size: 18px; font-weight: 600; color: var(--muted); }
  .wt-num[data-quality=missing], .wt-u[data-quality=missing] { display: none; }
  .wt-cap { font: 400 13px/1.35 var(--tc-font-sans); color: var(--muted); }
  .wt-cap:empty, .wt-att:empty { display: none; }
  .wt-att { font: 500 14px/1.35 var(--tc-font-sans); }
  .wt-att[data-tone=warn] { color: var(--attention); }
  .wt-att[data-tone=bad] { color: var(--danger); }
  .wt-att[data-tone=nosig] { color: var(--muted); }
  .wt-lo, .wt-lon { display: none; }
  .wt-lo[data-state=on] + .wt-lon { display: block; }
  .wt-rows { display: flex; flex-direction: column; margin: -4px 0; padding: 0; list-style: none; }
  .wt-r { display: grid; grid-template-columns: auto minmax(0, max-content) minmax(48px, 1fr) auto; align-items: center;
    column-gap: 10px; font: 400 14px/1.3 var(--tc-font-sans); }
  .wt-g { order: -1; width: 14px; height: 14px; fill: var(--_ci); }
  .wt-r > * { margin-block: 4px; }
  .wt-l { overflow-wrap: anywhere; }
  .wt-l:empty, .wt-l:empty ~ span, .wt-l:empty ~ svg { display: none; }
  .wt-bar { position: relative; height: 8px; border-radius: 999px; overflow: hidden; }
  .wt-t, .wt-f { position: absolute; top: 0; bottom: 0; left: 0; border-radius: 999px; }
  .wt-t { right: 0; background: var(--track); }
  .wt-f { background: var(--_c); width: calc(var(--frac,0) * 100%); }
  .wt-f[data-quality=stale] { opacity: 0.5; background: var(--quiet); }
  /* No level: a dashed empty lane over the track, never an empty tank (today's board-tank__bar--empty). */
  .wt-f[data-zone=nosig] { box-sizing: border-box; width: 100%; opacity: 1; background: var(--background);
    border: 1px dashed var(--grid); }
  .wt-wm { display: none; width: 14px; height: 14px; margin-right: 4px; vertical-align: -2px; }
  .wt-l[data-zone=warn] ~ .wt-v .wt-wm { display: inline; color: var(--attention); }
  .wt-l[data-zone=bad] ~ .wt-v .wt-wm { display: inline; color: var(--danger); }
  .wt-v { display: flex; flex-direction: column; align-items: flex-end; font: 600 15px var(--tc-font-rounded);
    font-variant-numeric: tabular-nums; white-space: nowrap; }
  .wt-v [data-quality=stale], .wt-v [data-quality=missing] { color: var(--muted); }
  /* At night a stale value keeps the text colour: grey under the night tint reads below the app's own values (§9.6, TOM-665). */
  html[data-theme=night] .wt-num[data-quality=stale], html[data-theme=night] .wt-v [data-quality=stale] { color: var(--text); }
  .wt-vu { font: 600 12px var(--tc-font-sans); color: var(--muted); margin-left: 0.25em; }
  .wt-vu[data-zone=nosig] { display: none; }
  .wt-a { font: 400 12px var(--tc-font-sans); color: var(--muted); }
  .wt-a:empty { display: none; }
  .wt-empty { grid-column: 1 / -1; font: 400 15px/1.4 var(--tc-font-sans); color: var(--muted); }
  .wt-l:not(:empty) ~ .wt-empty { display: none; }
`);

/** card.water: the boat's fresh water through its water feed (§8.3). */
export function cardWater() {
  const f = (field, extra = {}) => ({
    "data-tc-feed": "water",
    "data-tc-field": field,
    ...extra,
  });
  const rows = [];
  for (let k = 0; k < ROWS; k += 1) {
    const slot = { "data-tc-slot": k };
    rows.push(
      h(
        "li",
        { class: "wt-r" },
        h("span", { class: "wt-l", ...f("label", slot) }),
        h(
          "svg",
          { class: "wt-g", viewBox: "0 0 24 24", "aria-hidden": "true" },
          h("use", { href: "#wd" }),
        ),
        // The track under the fill, so a tank with no level can draw its
        // dashed empty lane over it.
        h(
          "span",
          { class: "wt-bar" },
          h("span", { class: "wt-t" }),
          h("span", { class: "wt-f", ...f("bar", slot) }),
        ),
        h(
          "span",
          { class: "wt-v" },
          h(
            "span",
            {},
            warnMark("wt-wm", "ww"),
            h("span", f("value", slot)),
            h("span", { class: "wt-vu", ...f("unit", slot) }),
          ),
          h("span", { class: "wt-a", ...f("age", slot) }),
        ),
        k === 0
          ? h(
              "p",
              { class: "wt-empty" },
              "The boat reports no fresh water tanks.",
            )
          : "",
      ),
    );
  }
  const html = h(
    "div",
    { class: "tp wt", "data-domain": "water" },
    // The droplet each row uses (DESIGN_SYSTEM §4), drawn once.
    h(
      "svg",
      { class: "wt-defs", "aria-hidden": "true" },
      h(
        "defs",
        {},
        h("path", {
          id: "wd",
          d: "M12 2.5C12 2.5 4.5 11 4.5 15.5a7.5 7.5 0 0 0 15 0C19.5 11 12 2.5 12 2.5Z",
        }),
        warnShape("ww"),
      ),
    ),
    h(
      "div",
      { class: "wt-hero" },
      h("span", { class: "wt-num", ...f("hero") }),
      h("span", { class: "wt-u", ...f("hero-unit") }),
      h("span", { class: "wt-cap", ...f("of") }),
    ),
    h("p", { class: "wt-cap", ...f("fullness") }),
    h("p", { class: "wt-att", ...f("attention") }),
    // The host writes no text into the levels-only flag, only its state: the
    // note beside it shows while it is on.
    h("span", { class: "wt-lo", ...f("levels-only") }),
    h(
      "p",
      { class: "wt-cap wt-lon" },
      "The tanks don’t say how much they hold, so the board shows how full each one is.",
    ),
    h("ul", { class: "wt-rows" }, rows),
    h("p", { class: "wt-cap", ...f("last-heard") }),
    h("p", { class: "wt-cap", ...f("forecast") }),
  );
  return output("card.water", 1, {
    html,
    css:
      CSS +
      css(
        `.wt-defs { position: absolute; width: 0; height: 0; overflow: hidden; }`,
      ) +
      domainCss(["water"], { icon: true, base: false }),
    feeds: [{ id: "water", kind: "water" }],
    card: {
      kind: "status",
      icon: "water",
      meta: { feed: "water", field: "as-of" },
    },
  });
}
